import { ReactNode, useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useDeleteModal } from '@components/generic-modals';
import { FormatPresetsProvider } from '@components/text-editors';
import { useFormatPickerModal } from '@components/text-editors/addons/format-picker-modal';
import { FormatPresetData } from '@components/text-editors/helpers';
import { uuid } from '@utils/uuid';

import { useCurrentUser } from 'api/authentication/hooks';
import { useCompanyFormatPresets, useEditCompanyDetails } from 'api/company/hooks';
import { CompanyFormatPreset, CompanyFormatPresets } from 'api/company/requests';

export const CompanyFormatPresetsProvider = ({ children }: { children: ReactNode }) => {
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const queryClient = useQueryClient();
  const { data: allPresets } = useCompanyFormatPresets();
  const { mutate: editCompany } = useEditCompanyDetails({ shouldNotShowSuccess: true });
  const showFormatPicker = useFormatPickerModal();
  const showDeleteModal = useDeleteModal();

  const datePresets = useMemo(() => allPresets?.date ?? [], [allPresets?.date]);

  const updateDatePresets = useCallback(
    (updated: CompanyFormatPreset[]) => {
      const nextPresets = { ...allPresets, date: updated };
      queryClient.setQueryData<CompanyFormatPresets>(['company-format-presets'], nextPresets);
      editCompany({ formatPresets: nextPresets });
    },
    [allPresets, editCompany, queryClient]
  );

  const onSaveFormatPreset = useCallback(
    (preset: Omit<CompanyFormatPreset, 'id'>) => {
      updateDatePresets([...datePresets, { ...preset, id: uuid() }]);
    },
    [datePresets, updateDatePresets]
  );

  const onEditFormatPreset = useCallback(
    (
      preset: FormatPresetData,
      options?: { onApply?: (format: string, formatLanguage?: string) => void }
    ) => {
      showFormatPicker({
        isEditing: true,
        initialFormat: preset.format,
        initialLanguage: preset.formatLanguage,
        initialName: preset.name,
        onApply: (format, formatLanguage, name) => {
          updateDatePresets(
            datePresets.map((presetItem) =>
              presetItem.id === preset.id
                ? { ...presetItem, format, formatLanguage, name: name ?? presetItem.name }
                : presetItem
            )
          );
          options?.onApply?.(format, formatLanguage);
        },
      });
    },
    [datePresets, updateDatePresets, showFormatPicker]
  );

  const onRemoveFormatPreset = useCallback(
    (id: string) => {
      if (datePresets.length <= 1) return;
      const preset = datePresets.find((preset) => preset.id === id);
      showDeleteModal({
        itemTitle: preset?.name ?? '',
        onConfirm: () => updateDatePresets(datePresets.filter((preset) => preset.id !== id)),
      });
    },
    [datePresets, updateDatePresets, showDeleteModal]
  );

  return (
    <FormatPresetsProvider
      formatPresets={datePresets}
      onSaveFormatPreset={isAdmin ? onSaveFormatPreset : undefined}
      onEditFormatPreset={isAdmin ? onEditFormatPreset : undefined}
      onRemoveFormatPreset={isAdmin ? onRemoveFormatPreset : undefined}
    >
      {children}
    </FormatPresetsProvider>
  );
};
